import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles, LineChart, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import PrivacyDisclaimer from "@/components/PrivacyDisclaimer";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_planning-onboard/artifacts/79k1cvlq_DSC06616.jpg";

export default function Landing() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen text-[#1C1D1C] page-tinted relative overflow-hidden">
      {/* Soft texture orbs */}
      <div aria-hidden className="pointer-events-none absolute -top-32 -right-32 h-[520px] w-[520px] rounded-full bg-[#F4B860] opacity-[0.18] blur-[120px]"/>
      <div aria-hidden className="pointer-events-none absolute top-[40%] -left-40 h-[460px] w-[460px] rounded-full bg-[#A7C7E7] opacity-[0.22] blur-[110px]"/>
      <div aria-hidden className="pointer-events-none absolute bottom-0 right-1/3 h-[380px] w-[380px] rounded-full bg-[#F4B860] opacity-[0.12] blur-[100px]"/>

      <nav className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between py-6 relative">
        <Logo/>
        <div className="flex items-center gap-2">
          <Link to="/admin/login" data-testid="nav-admin-link" className="text-sm text-[#5B5E5B] hover:text-[#1C1D1C] px-4 py-2 rounded-full hover:bg-white/60 transition-colors">
            Advisor login
          </Link>
          <Button
            data-testid="nav-cta-google"
            onClick={startLogin}
            className="rounded-full bg-[#1A3626] hover:bg-[#2C4C3B] text-white px-6 h-11"
          >
            Get started
          </Button>
        </div>
      </nav>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-24 md:pb-32 grid lg:grid-cols-12 gap-12 items-center relative">
        <div className="lg:col-span-7 fade-up">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">A modern financial intake</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mt-4 leading-[1.05]">
            Share your numbers once.<br/>
            <span className="text-[#1A3626] italic">Get a plan that fits your life.</span>
          </h1>
          <p className="text-lg text-[#5B5E5B] leading-relaxed mt-6 max-w-xl">
            A private, bank-grade onboarding for clients of Wealth Accelerator Hub.
            Submit your family, income, expenses, assets, loans, investments, insurance and goals — your advisor builds the rest.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Button data-testid="hero-cta-google" onClick={startLogin}
              className="rounded-full bg-[#1A3626] hover:bg-[#2C4C3B] text-white h-12 px-8 text-sm font-medium hover:-translate-y-0.5 transition-all">
              Continue with Google <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5}/>
            </Button>
            <div className="flex items-center gap-2 text-sm text-[#8A8D8A] px-4">
              <Lock className="h-4 w-4" strokeWidth={1.5}/> Encrypted • Private • Advisor-only access
            </div>
          </div>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="relative rounded-3xl overflow-hidden border border-[#E5E2DA] shadow-[0_20px_60px_rgba(28,29,28,0.10)] bg-white">
            <img src={HERO_IMG} alt="Advisor" className="w-full h-[520px] object-cover"/>
            <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-md rounded-2xl p-5 border border-[#E5E2DA]">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Your advisor</div>
              <div className="font-heading text-xl mt-1">Personal. Independent. Fiduciary.</div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 relative">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, title: "Bank-grade privacy", body: "Your data is encrypted and visible only to your advisor — never sold, never shared." },
            { icon: Sparkles, title: "Comprehensive intake", body: "An eight-step datasheet covering family, income, expenses, assets, loans, SIPs, insurance & goals." },
            { icon: LineChart, title: "Instant snapshot", body: "See net worth, EMI ratio, savings rate and protection gap the moment you submit." },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="rounded-2xl border border-[#E5E2DA] bg-white/80 backdrop-blur p-8 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
              <Icon className="h-6 w-6 text-[#1A3626]" strokeWidth={1.5}/>
              <h3 className="font-heading text-xl font-medium mt-5">{title}</h3>
              <p className="text-[#5B5E5B] mt-2 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white/60 backdrop-blur py-24 md:py-32 border-y border-[#E5E2DA]/60 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">What you&apos;ll share</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight mt-4">
              Eight short steps. Everything your planner needs.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {["Family", "Income", "Expenses", "Assets", "Loans", "Investments", "Insurance", "Goals"].map((t, i) => (
              <div key={`${t}-${i}`} className="bg-white rounded-2xl border border-[#E5E2DA] p-6">
                <div className="mono text-xs text-[#C97B2A]">0{i+1}</div>
                <div className="font-heading text-lg mt-3">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center relative">
        <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight">Ready when you are.</h2>
        <p className="text-lg text-[#5B5E5B] mt-4">Sign in with Google to begin your financial profile.</p>
        <div className="mt-10 flex justify-center">
          <Button data-testid="footer-cta-google" onClick={startLogin}
            className="rounded-full bg-[#1A3626] hover:bg-[#2C4C3B] text-white h-12 px-8 hover:-translate-y-0.5 transition-all">
            Continue with Google <ArrowRight className="ml-2 h-4 w-4"/>
          </Button>
        </div>
      </section>

      <PrivacyDisclaimer/>

      <footer className="border-t border-[#E5E2DA] py-10 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col sm:flex-row justify-between text-sm text-[#8A8D8A]">
          <div>© {new Date().getFullYear()} Wealth Accelerator Hub — Private client intake</div>
          <Link data-testid="footer-admin-link" to="/admin/login" className="hover:text-[#1C1D1C]">Advisor portal →</Link>
        </div>
      </footer>
    </div>
  );
}
